import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, ShieldOff } from "lucide-react";

export default function AdminPersonal() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);

  const reset2fa = async (id) => {
    if (!window.confirm("¿Resetear 2FA de este usuario?")) return;
    try {
      await api.post(`/superadmin/users/${id}/reset-2fa`);
      toast.success("2FA reseteado");
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Personal</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Staff, instructores y admins</h1>
          </div>
          <button onClick={() => setShowModal(true)} data-testid="btn-new-staff"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo miembro
          </button>
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Roles</th>
                <th className="text-left px-3 py-2">2FA</th>
                <th className="text-right px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-t border-gray-800">
                  <td className="px-3 py-2 text-white">{s.full_name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-300">{s.email}</td>
                  <td className="px-3 py-2 text-gray-300 text-xs">{(s.roles || []).join(", ")}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${s.totp_enabled ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-gray-500/10 text-gray-400 border border-gray-700"}`}>
                      {s.totp_enabled ? "activo" : "inactivo"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {superadmin && s.totp_enabled && (
                      <button onClick={() => reset2fa(s.id)} className="text-xs text-[#93C5FD] hover:text-white inline-flex items-center gap-1">
                        <ShieldOff className="w-3 h-3" /> Reset 2FA
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && <NewStaffModal superadmin={superadmin} onClose={() => { setShowModal(false); load(); }} />}
    </StaffLayout>
  );
}

function NewStaffModal({ superadmin, onClose }) {
  const [form, setForm] = useState({ email: "", full_name: "", role: "staff_dcc" });
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/staff", form);
      setResult(data);
      toast.success("Usuario creado");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-semibold text-white">Nuevo miembro</h3>
          <button onClick={onClose} className="text-gray-400"><X /></button>
        </div>
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div><label className="text-[11px] uppercase tracking-widest text-gray-400">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[11px] uppercase tracking-widest text-gray-400">Nombre</label>
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[11px] uppercase tracking-widest text-gray-400">Rol</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
                <option value="staff_dcc">Staff DCC</option>
                <option value="instructor">Instructor</option>
                {superadmin && <option value="superadmin_dcc">Superadmin DCC</option>}
              </select></div>
            <button type="submit" className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold">Crear</button>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="text-emerald-300">Creado. Contraseña temporal:</p>
            <p className="font-mono text-white bg-[#0B0F14] p-2 rounded border border-gray-700">{result.temp_password}</p>
          </div>
        )}
      </div>
    </div>
  );
}
